import type { Prompt } from "@1js/pl-types";
import React from "react";
import { useFlightContext } from "../../hooks/useFlightContext";
import { LibraryContext } from "../../pages/prompts/allPromptsLibrary/LibraryContext";
import { deleteSharedPromptFromWorkgroup } from "../../services/prompt-client.web";
import useTelemetry from "../../utils/Telemetry/useTelemetry";
import type { HandlePromptsTabSelected, Notify } from "./AllPrompts.web.data";
import { useWorkgroups } from "./useWorkgroups.hook";

export type UseWorkgroupPromptsProps = {
  notify: Notify;
  promptRemovedFromTeamToastErrorTitle: () => string;
  promptRemovedFromTeamToastTitle: () => string;
  handlePromptsTabSelected: HandlePromptsTabSelected;
};

const PromptRemovalProgressWaitTime = 1000;

export const useWorkgroupPrompts = ({
  notify,
  promptRemovedFromTeamToastErrorTitle,
  promptRemovedFromTeamToastTitle,
  handlePromptsTabSelected,
}: UseWorkgroupPromptsProps) => {
  useWorkgroups({ handlePromptsTabSelected, isWorkgroupTabShowing: true });
  const telemetry = useTelemetry();
  const { isFlightActive } = useFlightContext();
  const { state, dispatch } = React.useContext(LibraryContext);
  const [removeDialogConfig, setRemoveDialogConfig] = React.useState<{
    isOpen: boolean;
    prompts: Prompt[] | undefined;
    groupIds: string[] | undefined;
  }>({
    isOpen: false,
    prompts: undefined,
    groupIds: undefined,
  });

  const handleOnRemoveFromWorkgroupClicked = React.useCallback(
    (prompts: Prompt[], groupIds: string[]) => {
      telemetry.sendPromptActionEvent(
        "WorkgroupDelete",
        "PromptAction",
        "RemoveButton",
        prompts[0],
        false
      );
      setRemoveDialogConfig({
        isOpen: true,
        prompts,
        groupIds,
      });
    },
    [telemetry]
  );

  const handleOnRemoveClose = React.useCallback(
    (isClosedByCustomer: boolean) => {
      if (isClosedByCustomer) {
        telemetry.sendPromptActionEvent(
          "WorkgroupView",
          "Dialog",
          "CancelButton",
          removeDialogConfig.prompts?.[0] as Prompt
        );
      }
      setRemoveDialogConfig({
        isOpen: false,
        prompts: undefined,
        groupIds: undefined,
      });
    },
    [removeDialogConfig.prompts, telemetry]
  );
  const serviceHost = state.serviceHost;
  const handleOnRemoveFromWorkgroupRemoveClicked = React.useCallback(
    async (prompts: Prompt[], groupIds: string[]) => {
      telemetry.sendPromptActionEvent(
        "WorkgroupDelete",
        "Dialog",
        "RemoveButton",
        prompts[0],
        false
      );
      try {
        dispatch({
          type: "REMOVE_GROUP_PROMPT_INIT",
          payload: { prompts, groupIds },
        });

        setTimeout(() => {
          // dispatch could be undefined if the component has been unmounted already
          dispatch?.({
            type: "REMOVE_GROUP_PROMPT_LONG",
            payload: { prompts, groupIds },
          });
        }, PromptRemovalProgressWaitTime);

        const deletedPrompts = await deleteSharedPromptFromWorkgroup(
          serviceHost,
          prompts,
          groupIds,
          telemetry.sendApiActionEvent
        );
        if (deletedPrompts.Prompts.length !== prompts.length) {
          notify("error", promptRemovedFromTeamToastErrorTitle());
          return;
        }

        notify(
          "success",
          promptRemovedFromTeamToastTitle(),
          undefined,
          undefined
        );
        dispatch({
          type: "REMOVE_GROUP_PROMPT_SUCCESS",
          payload: {
            groupIds,
            promptsToBeDeleted: prompts,
            deletedPrompts: deletedPrompts.Prompts,
          },
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        notify("error", promptRemovedFromTeamToastErrorTitle());
      }
    },
    [
      dispatch,
      notify,
      promptRemovedFromTeamToastErrorTitle,
      promptRemovedFromTeamToastTitle,
      serviceHost,
      telemetry,
    ]
  );

  const handleOnRemoveDialogClicked = React.useCallback(() => {
    !!removeDialogConfig.prompts &&
      !!removeDialogConfig.groupIds &&
      handleOnRemoveFromWorkgroupRemoveClicked(
        removeDialogConfig.prompts,
        removeDialogConfig.groupIds
      );
    handleOnRemoveClose(false);
  }, [
    handleOnRemoveClose,
    handleOnRemoveFromWorkgroupRemoveClicked,
    removeDialogConfig.groupIds,
    removeDialogConfig.prompts,
  ]);

  return {
    handleOnRemoveClose,
    handleOnRemoveDialogClicked,
    handleOnRemoveFromWorkgroupClicked,
    removeDialogConfig,
    isFlightActive,
  };
};
